import { useQuery } from 'react-query';
import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { DEFAULT_STALE_TIME } from '../../../../constants';
import { createAcctConfig, getOnboardingBaseData } from 'api';

// Hooks, Atoms (app state)
import { useOnboardAcctConfigAtom, useOnboardHSAConfigAtom } from 'atoms';
import { useIsRecalculatingHSA } from 'atoms/isRecalculatingHSA';
import { useAtom } from 'jotai';
import { isDirtyAtom } from 'atoms/isDirtyAtom';
import { useDebounce } from 'hooks/useDebounce';
import useFieldChangeTracker from 'hooks/useFieldChangeTracker';

// Components
import AccountInputs from './AccountInputs';
import GSCPropertyInput from './GSCPropertyInput';
import Textbox from '../../../../components/Textbox';
import OnboardContainer from '../../OnboardContainer';
import Select, { SingleSelection } from '../../../../components/Select';
import { selectedCustomerAtom } from 'atoms/useSelectedCustomerAtom';

// Types
import { CreateAcctConfigBody } from 'types';

// Utils
import {
  isValidGoogleAdsID,
  isValidGSCProperty,
  sanitizeAcctNumInput,
  onBlurValidateGoogleAdsID,
} from '../../../../utils';

const hsaStatusBadgeClassName = {
  Draft: 'bg-slate-100 text-black',
  Processing: 'bg-purple-100 text-yellow800',
  Done: 'bg-disgreen100 text-disgreen800',
  Failed: 'bg-red-200 text-red800',
  default: 'bg-slate-100 text-black',
};

// Helper to convert date and time to local time (TODO: Move to a Utils folder and apply same formatting to other dates)
export const toLocalTimeFormat = (dateVariable?: string | Date | null) => {
  if (!dateVariable) return '';

  const date =
    dateVariable instanceof Date ? dateVariable : new Date(dateVariable);

  if (isNaN(date.getTime())) return ''; // invalid date

  return date.toLocaleString('en-IN', {
    weekday: 'short',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
};

// HSA PROPERTIES FORM
function HSAPropertiesForm() {
  const [isRecalculatingHSA] = useIsRecalculatingHSA();
  const [selectedCustomer, setSelectedCustomer] = useAtom(selectedCustomerAtom);
  const [, setIsDirty] = useAtom(isDirtyAtom);
  const [hsaConfig] = useOnboardHSAConfigAtom();
  const [, setAcctConfig] = useOnboardAcctConfigAtom();

  const [managerMCC, setManagerMCC] = useState('');
  const [currency, setCurrency] = useState<SingleSelection>(null);
  const [googleEmail, setGoogleEmail] = useState<SingleSelection>(null);

  const [accounts, setAccounts] = useState([
    { account_name: '', account_number: '' },
  ]);

  const [gscProperties, setGSCProperties] = useState(['']);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [changeCount, handleFieldChange] = useFieldChangeTracker(); // Custom hook increments number to detect input change to trigger mutation - auto save
  const [isValid, setIsValid] = useState(false);

  // Form field values from saved draft or previous HSA
  const googleEmailSavedDraft = selectedCustomer?.googleEmail ?? ''; // Google Email
  const accountConfig = selectedCustomer?.account_configurations?.[0];
  const googleAdsConfig = accountConfig?.google_ads;

  // GSC Property
  const gscPropertiesSavedDraft =
    accountConfig?.google_search_console?.properties;

  // Manager MCC
  const managerMCCSavedDraft = googleAdsConfig?.login_customer_id;

  // Account Names
  const accountNameSavedDraft = useMemo(() => {
    return googleAdsConfig?.account_name ?? [];
  }, [googleAdsConfig?.account_name]);

  // Account Numbers
  const accountNumberSavedDraft = useMemo(() => {
    return googleAdsConfig?.customer_id ?? [];
  }, [googleAdsConfig?.customer_id]);

  // Currency
  const currencyCodeSavedDraft = accountConfig?.currency_code;

  // Created on and Last updated on
  const createdAt = toLocalTimeFormat(selectedCustomer?.created_at);
  const updatedAt = toLocalTimeFormat(selectedCustomer?.updated_at);

  // Assessment ID
  const hsaID = selectedCustomer?.hsa_id ?? '';
  const hsaStatus = selectedCustomer?.hsa_status ?? 'No';
  const hsaStatusClassName =
    hsaStatusBadgeClassName[hsaStatus as keyof typeof hsaStatusBadgeClassName] ??
    hsaStatusBadgeClassName.default;

  // Check if new HSA (To show or hide the HSA Details header etc)
  const newHSA = hsaStatus === 'No';

  // Handles auto save for Select drop downs (Auto save is enabled only for Draft/Recalulate HSA's)
  const handleAutoSave = () => {
    setIsValid(true);
    handleFieldChange();
  };

  const onChangeAcctName = useCallback((val: string, index: number) => {
    setAccounts((prev) => {
      const next = [...prev];
      next[index] = {
        ...next[index],
        account_name: val,
      };
      return next;
    });

    setIsValid(val.trim().length > 0);
    handleFieldChange();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { data } = useQuery({
    queryKey: ['onboardingBaseData'],
    queryFn: getOnboardingBaseData,
    staleTime: DEFAULT_STALE_TIME,
  });

  const googleEmailOptions = useMemo(() => {
    return data?.google_email_accounts.map((email) => ({
      label: email,
      value: email,
    }));
  }, [data]);

  // Prefill, Update with previous HSA data if available (For the Recalculate & Draft work-flow)
  useEffect(() => {
    if (!googleAdsConfig) return;

    setGoogleEmail({
      label: googleEmailSavedDraft,
      value: googleEmailSavedDraft,
    });

    if (managerMCCSavedDraft) {
      setManagerMCC(managerMCCSavedDraft);
    }

    setAccounts(() => {
      const maxLen = Math.max(
        accountNumberSavedDraft.length,
        accountNameSavedDraft.length
      );

      return Array.from({ length: maxLen }).map((_, i) => ({
        account_number: accountNumberSavedDraft[i] ?? '',
        account_name: accountNameSavedDraft[i] ?? '',
      }));
    });

    if (currencyCodeSavedDraft) {
      setCurrency({
        label: currencyCodeSavedDraft,
        value: currencyCodeSavedDraft,
      });
    }

    // Google Search Console (GSC) related prefill
    if (gscPropertiesSavedDraft?.length) {
      // Remove empty values
      const validProperties = gscPropertiesSavedDraft
        .map((p) => p.trim())
        .filter((p) => p !== '');

      if (validProperties.length > 0) {
        setGSCProperties(validProperties);
      }
    }
  }, [
    accountNameSavedDraft,
    currencyCodeSavedDraft,
    googleAdsConfig,
    googleEmailSavedDraft,
    gscPropertiesSavedDraft,
    managerMCCSavedDraft,
    accountNumberSavedDraft,
  ]);

  // Compare all current form fields with last saved data and set isDirty flag to true or false using "changed"
  const changed = useMemo(() => {
    const accountNameForm = accounts.map((item) => item.account_name);
    const accountNumberForm = accounts.map((item) => item.account_number);

    // Compare the Account Name saved values to current form Account Name value
    const accountNameChanged =
      accountNameSavedDraft?.length !== accountNameForm?.length ||
      !accountNameSavedDraft.every(
        (value, index) => value === accountNameForm[index]
      );

    // Compare the Account Number saved values to current form Account Number value
    const accountNumberChanged =
      accountNumberSavedDraft?.length !== accountNumberForm?.length ||
      !accountNumberSavedDraft.every(
        (value, index) => value === accountNumberForm[index]
      );

    // Compare the GSC Property saved values to current form GSC Property value
    const gscPropertiesChanged =
      gscPropertiesSavedDraft?.length !== gscProperties?.length ||
      !gscPropertiesSavedDraft.every(
        (value, index) => value === gscProperties[index]
      );

    return (
      // If any form values changed compared to previous (Saved) return true
      googleEmail?.value !== googleEmailSavedDraft ||
      managerMCCSavedDraft !== managerMCC ||
      accountNameChanged ||
      accountNumberChanged ||
      gscPropertiesChanged ||
      currencyCodeSavedDraft !== currency?.value
    );
  }, [
    accounts,
    accountNameSavedDraft,
    accountNumberSavedDraft,
    gscPropertiesSavedDraft,
    gscProperties,
    googleEmail?.value,
    googleEmailSavedDraft,
    managerMCCSavedDraft,
    managerMCC,
    currencyCodeSavedDraft,
    currency?.value,
  ]);

  // Set isDirty flag for Properties page if any form value changed since last saved
  useEffect(() => {
    setIsDirty((prev) => ({ ...prev, Properties: changed }));
  }, [changed, setIsDirty]);

  const onChangeMCC = (
    value: string,
    evt: React.ChangeEvent<HTMLInputElement>
  ) => {
    const santizedValue = sanitizeAcctNumInput(value, evt, managerMCC);

    if (santizedValue !== managerMCC) {
      setManagerMCC(santizedValue);
    }
    // Required validation for autosave (Recalculate/Draft flow) - This is required as original validation only triggers on blur
    setIsValid(isValidGoogleAdsID(value));
    handleFieldChange();
  };

  const onChangeAcctNumber = useCallback((value: string, index: number) => {
    setAccounts((prev) => {
      prev[index].account_number = value;
      return [...prev];
    });
    // To trigger auto save if field valid and value changed
    setIsValid(isValidGoogleAdsID(value));
    handleFieldChange();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onAddAccount = () => {
    setAccounts((prev) => [...prev, { account_name: '', account_number: '' }]);
  };

  const onRemoveAccount = useCallback((index: number) => {
    setAccounts((prev) => prev.filter((_, acctIndex) => acctIndex !== index));
  }, []);

  const onChangeGSCProperty = useCallback((val: string, index: number) => {
    const trimmed = val.trim();
    setGSCProperties((prev) => {
      prev[index] = trimmed;
      return [...prev];
    });
    // To trigger auto save if field valid and value changed
    setIsValid(isValidGSCProperty(trimmed));
    handleFieldChange();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onAddGSCProperty = () => {
    setGSCProperties((prev) => [...prev, '']);
  };

  const onRemoveGSCProperty = useCallback(
    (index: number) => {
      setGSCProperties((prev) =>
        prev.filter((_, gscIndex) => gscIndex !== index)
      );
    },
    [setGSCProperties]
  );

  const currencyOptions = useMemo(() => {
    if (!data?.currency_data) return [];

    const uniqueCurrencies = new Set<string>();

    for (const { currency_name, currency_code } of data.currency_data) {
      uniqueCurrencies.add(`${currency_name}-${currency_code}`);
    }

    return Array.from(uniqueCurrencies)
      .sort()
      .map((val) => ({
        label: val,
        value: val,
      }));
  }, [data?.currency_data]);

  const isDisabled = useMemo(() => {
    if (
      !googleEmail?.value ||
      !currency?.value ||
      !isValidGoogleAdsID(managerMCC)
    ) {
      return true;
    }

    // Validate Account inputs
    for (const { account_name, account_number } of accounts) {
      if (!account_name.trim() || !isValidGoogleAdsID(account_number))
        return true;
    }

    // Validate GSC Properties inputs
    for (const gscProp of gscProperties) {
      if (!isValidGSCProperty(gscProp.trim())) return true;
    }

    return false;
  }, [googleEmail, currency, managerMCC, accounts, gscProperties]);

  const onSubmit = async () => {
    if (!hsaConfig?.customer_id) {
      throw new Error('Customer ID is required');
    }
    if (!currency?.value) {
      throw new Error('Currency value is required.');
    }
    if (!googleEmail?.value) {
      throw new Error('Google email is required.');
    }

    const customer_id: string[] = [];
    const account_name: string[] = [];

    for (const account of accounts) {
      account_name.push(account.account_name);
      customer_id.push(account.account_number.replace(/-/g, ''));
    }

    const body: CreateAcctConfigBody = {
      google_ads: {
        customer_id,
        account_name,
        login_customer_id: managerMCC.replace(/-/g, ''),
      },
      google_search_console: {
        properties: gscProperties.map((value) => value),
      },
      currency_code: currency.value,
      google_email_account: googleEmail.value,
    };

    const res = await createAcctConfig(hsaConfig.customer_id, body);
    setAcctConfig(res.data);

    // Update atom with the new HSA form data for Properties page to update local state
    setSelectedCustomer((prev) => ({
      ...prev,
      googleEmail: googleEmail.value,
      account_configurations: prev.account_configurations.map((config, index) =>
        index === 0
          ? {
              ...config,
              google_ads: {
                ...config.google_ads,
                ...body.google_ads,
                google_ads_status:
                  config.google_ads?.google_ads_status ?? 'active',
              },
              google_search_console: {
                ...config.google_search_console,
                ...body.google_search_console,
                google_search_console_status:
                  config.google_search_console?.google_search_console_status ??
                  'active',
              },
              currency_code: body.currency_code ?? config.currency_code,
            }
          : config
      ),
    }));

    return res;
  };

  // JSX
  return (
    <OnboardContainer
      header="Properties"
      onSubmit={onSubmit}
      isDisabled={isDisabled}
      changeCount={changeCount} // changeCount - to check for and trigger auto save - on change of form fields TODO: Refactor code and remove this variable/prop
      isValidInput={isValid}
    >
      <>
        {/* --- Header to show HSA status and details - Only for Status = Draft, Done, Failed - Do not show this for New HSA (NO status)) --- */}
        {!newHSA && (
          <>
            <div className="relative flex flex-col gap-1 bg-white shadow-md rounded-xl p-6 ">
              {/* Top row - Heading & Status with collapse arrow */}
              <div className="flex justify-between items-center mb-4">
                <h1 className="text-xl font-semibold">
                  Historical Savings Assessment Details
                </h1>

                <div className="flex items-center gap-3 ">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      setIsCollapsed(!isCollapsed);
                    }}
                    className="p-1 hover:bg-gray-100 rounded-full"
                  >
                    {isCollapsed ? (
                      <span style={{ fontSize: '32px' }}>⌄</span>
                    ) : (
                      <span style={{ fontSize: '32px' }}>⌃</span>
                    )}
                  </button>
                </div>
              </div>

              {/* Show details only if expanded */}
              {!isCollapsed && (
                <>
                  {/* Second row - Assessment Id */}
                  <div className="flex flex-row gap-12 mb-6">
                    <span className="flex flex-col">
                      <span>Assessment Id:</span>
                      <span>{hsaID}</span>
                    </span>
                    <span className="flex flex-col">
                      <span> Status:</span>
                      <span
                        className={`px-3 py-1 ${hsaStatusClassName} text-sm font-semibold border border-black/20`}
                      >
                        {hsaStatus}
                      </span>
                    </span>
                  </div>

                  {/* Third row - Created/Modified info */}
                  <div className="flex items-center gap-6 text-sm text-gray-600">
                    <span className="flex flex-col">
                      <span>Created on:</span>
                      <span>{createdAt}</span>
                    </span>

                    <span className="flex flex-col">
                      <span>Last updated on:</span>
                      <span>{updatedAt}</span>
                    </span>
                  </div>
                </>
              )}
            </div>
          </>
        )}

        {/* --- Client & Agency - Read only values - rendered on Recalculate Flow --- */}
        {isRecalculatingHSA && (
          <>
            <Textbox
              label="Client Name"
              value={selectedCustomer?.client}
              required
              readOnly
            />
            <Textbox
              label="Agency"
              value={selectedCustomer?.agency}
              required
              readOnly
            />
          </>
        )}
        {/* --- Google Account Email --- */}
        <div className="lg:w-1/2">
          <Select
            label="Google Email"
            tooltip="onboarding.google_account"
            placeholder="Select Account Email"
            options={googleEmailOptions}
            onChange={(val) => {
              setGoogleEmail(val);
            }}
            value={googleEmail}
            required
            handleAutoSave={handleAutoSave}
          />
        </div>

        {/* --- Manager MMC Input --- */}
        <Textbox
          label="Manager MCC"
          tooltip="onboarding.manager_mcc"
          placeholder="XXX-XXX-XXXX"
          onChange={onChangeMCC}
          value={managerMCC}
          pattern="^[0-9\-?]*$"
          onBlur={(evt) => {
            onBlurValidateGoogleAdsID(evt);
          }}
          required
        />

        {/* --- Account Inputs --- */}
        <div className="space-y-4">
          {accounts.map((value, index) => {
            return (
              <AccountInputs
                key={index}
                index={index}
                valueName={value.account_name}
                valueNumber={value.account_number}
                onRemove={onRemoveAccount}
                onChangeName={onChangeAcctName}
                onChangeNumber={onChangeAcctNumber}
                isRemoveable={accounts.length > 1 || index > 0}
              />
            );
          })}
          <button
            type="button"
            className="border border-solid border-gray400 text-gray400 text-sm px-2 rounded-full flex items-center gap-1"
            onClick={onAddAccount}
          >
            <span className="text-xl leading-none">+</span>Add Account
          </button>
        </div>

        {/* --- GSC Property Inputs --- */}
        <div className="space-y-4">
          {gscProperties.map((val, index) => {
            return (
              <GSCPropertyInput
                value={val}
                onChange={(val, index) => {
                  onChangeGSCProperty(val, index);
                }}
                onRemove={onRemoveGSCProperty}
                isRemoveable={gscProperties.length > 1 || index > 0}
                index={index}
                key={index}
              />
            );
          })}

          <button
            type="button"
            className="border border-solid border-gray400 text-gray400 text-sm px-2 rounded-full flex items-center gap-1"
            onClick={onAddGSCProperty}
          >
            <span className="text-xl leading-none">+</span>Add GSC Property
          </button>
        </div>

        {/* --- Currency (displayed within app) --- */}
        <div className="border-t border-t-gray-300 pt-8">
          <Select
            tooltip="onboarding.currency"
            placeholder="Select Currency"
            className="lg:w-1/2"
            menuPlacement="top"
            label="Currency"
            options={currencyOptions}
            value={currency}
            onChange={(val) => setCurrency(val)}
            required
            handleAutoSave={handleAutoSave}
          />
        </div>
      </>
    </OnboardContainer>
  );
}

export default HSAPropertiesForm;
